import React from 'react';

import Typography from '@mui/material/Typography';
import type { ChartRequest } from 'app-components/flux/ai-chat/aiChatTypes';
import AIChartPreview from 'components/Charts/AIChartPreview/AIChartPreview';
import { normalizeChartRequest } from 'components/Charts/AIChartPreview/chartPreviewUtils';

type Props = {
  chart: ChartRequest;
  height?: number | string;
  width?: number | string;
};

const SUPPORTED_CHART_TYPES: readonly ChartRequest['chartType'][] = [
  'pie',
  'bar',
  'sidewaysBar',
  'column',
  'line',
  'area',
];

export const ChartPreviewRouter = ({ chart, height, width }: Props): React.JSX.Element => {
  const normalizedChart = React.useMemo(() => normalizeChartRequest(chart), [chart]);
  const computedHeight = React.useMemo(() => {
    if (typeof height !== 'undefined') {
      return height;
    }
    const rowCount = normalizedChart.data?.length ?? 0;
    const minHeight = 240;
    const maxHeight = 520;
    const nextHeight = 120 + rowCount * 28;
    return Math.max(minHeight, Math.min(maxHeight, nextHeight));
  }, [height, normalizedChart.data]);

  if (!normalizedChart.categoryField || !normalizedChart.valueFields?.length || !normalizedChart.data?.length) {
    return (
      <Typography variant='captionRegular' color='textSecondary'>
        Chart preview unavailable (missing fields or data).
      </Typography>
    );
  }

  if (!SUPPORTED_CHART_TYPES.includes(normalizedChart.chartType)) {
    return (
      <Typography variant='captionRegular' color='textSecondary'>
        Chart preview unavailable (unsupported chart type).
      </Typography>
    );
  }

  return <AIChartPreview chart={normalizedChart} height={computedHeight} width={width} />;
};

const ChartPreview = ({ chart, height, width }: Props): React.JSX.Element => (
  <ChartPreviewRouter chart={chart} height={height} width={width} />
);

export default ChartPreview;
